import React, { useEffect, useState } from "react";
import { useTable, usePagination, useSortBy, useFilters, useGroupBy, useExpanded, useRowSelect } from "react-table";
import _ from "lodash";
import Select, { components } from "react-select";

// Create an editable cell renderer
const EditableCell = ({
    value: initialValue,
    row: { index },
    column: { id },
    updateMyData, // This is a custom function that we supplied to our table instance
    editable,
}) => {
    // We need to keep and update the state of the cell normally
    const [value, setValue] = React.useState(initialValue);

    const onChange = (e) => {
        setValue(e.target.value);
    };

    // We'll only update the external data when the input is blurred
    const onBlur = () => {
        updateMyData(index, id, value);
    };

    // If the initialValue is changed externall, sync it up with our state
    React.useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    if (!editable) {
        return `${initialValue}`;
    }

    return <input value={value} onChange={onChange} onBlur={onBlur} />;
};

// Define a default UI for filtering
function DefaultColumnFilter({ column: { filterValue, preFilteredRows, setFilter } }) {
    const count = preFilteredRows.length;

    return (
        <input
            className="form-control fs-12px"
            value={filterValue || ""}
            onChange={(e) => {
                setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
            }}
            placeholder={`Buscar (${count} registros)`}
        />
    );
}

// This is a custom filter UI for selecting
// a unique option from a list
function SelectColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }) {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) => {
            options.add(row.values[id]);
        });
        return [...options.values()];
    }, [id, preFilteredRows]);

    // Render a multi-select box
    return (
        <select
            className="form-select fs-12px"
            value={filterValue}
            onChange={(e) => {
                setFilter(e.target.value || undefined);
            }}>
            <option value="">Todos</option>
            {options.map((option, i) => (
                <option key={i} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}

const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;
    if (!hasValue) {
        return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }
    return <components.ValueContainer {...props}>{`${nbValues} item seleccionados`}</components.ValueContainer>;
};

function MultiSelectColumnFilter({ column: { filterValue = [], setFilter, preFilteredRows, id } }) {
    const components = { ValueContainer };

    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) => {
            options.add(row.values[id]);
        });
        return [...options.values()];
    }, [id, preFilteredRows]);

    const [selected, setSelected] = useState();

    useEffect(() => {
        setSelected(
            _.map(filterValue, (v) => ({
                value: v,
                label: v,
            }))
        );
    }, []);

    useEffect(() => {
        setFilter(_.map(selected, "value"));
    }, [selected]);

    return (
        <Select
            placeholder="Todos"
            isMulti
            hideSelectedOptions={false}
            value={selected}
            onChange={(selected) => setSelected(selected)}
            components={components}
            options={options.map((option) => ({
                value: option,
                label: option,
            }))}
        />
    );
}

function multiSelectFilter(rows, columnId, filterValue) {
    return filterValue.length === 0 ? rows : rows.filter((row) => filterValue.includes(String(row.values[columnId])));
}

function NumberRangeColumnFilter({ column: { filterValue = [], preFilteredRows, setFilter, id } }) {
    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0;
        preFilteredRows.forEach((row) => {
            min = Math.min(row.values[id], min);
            max = Math.max(row.values[id], max);
        });
        return [min, max];
    }, [id, preFilteredRows]);

    return (
        <div
            style={{
                display: "flex",
            }}>
            <input
                value={filterValue[0] || ""}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setFilter((old = []) => [val ? parseInt(val, 10) : undefined, old[1]]);
                }}
                placeholder={`Min (${min})`}
                style={{
                    width: "70px",
                    marginRight: "0.5rem",
                }}
            />
            to
            <input
                value={filterValue[1] || ""}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setFilter((old = []) => [old[0], val ? parseInt(val, 10) : undefined]);
                }}
                placeholder={`Max (${max})`}
                style={{
                    width: "70px",
                    marginLeft: "0.5rem",
                }}
            />
        </div>
    );
}

// Be sure to pass our updateMyData and the skipReset option
function Table({ columns, data, updateMyData, skipReset, selection, selectedListener, rowsDrawnListener, defaultFilterValues }) {
    const filterTypes = React.useMemo(
        () => ({
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
            // And also our default editable cell
            Cell: EditableCell,
            width: 50,
        }),
        []
    );

    // Use the state and functions returned from useTable to build your UI
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        selectedFlatRows,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page
        rows,
        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy, groupBy, expanded, filters, selectedRowIds },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: 15, ...defaultFilterValues },
            defaultColumn,
            filterTypes,
            // updateMyData isn't part of the API, but
            // anything we put into these options will
            // automatically be available on the instance.
            // That way we can call this function from our
            // cell renderer!
            updateMyData,
            // We also need to pass this so the page doesn't change
            // when we edit the data.
            autoResetPage: !skipReset,
            autoResetSelectedRows: !skipReset,
            disableMultiSort: true,
        },
        useFilters,
        useGroupBy,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,

        // Here we will use a plugin to add our selection column
        (hooks) => {
            hooks.visibleColumns.push((columns) => {
                if (!selection) return columns;
                return [
                    {
                        id: "selection",
                        // Make this column a groupByBoundary. This ensures that groupBy columns
                        // are placed after it
                        groupByBoundary: true,
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                            </div>
                        ),
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ];
            });
        }
    );

    useEffect(() => {
        if (selectedListener) {
            selectedListener(selectedFlatRows.map((d) => d.original));
        } else if (selection) {
            console.error("TableReact - selectedListener no definido como parametro");
        }
    }, [selectedFlatRows]);

    useEffect(() => {
        if (rowsDrawnListener) {
            rowsDrawnListener(_.map(rows, "original"));
        }
    }, [filters]);

    // Render the UI for your table
    return (
        <div className="table-responsive">
            <table className="table table-hover table-striped table-panel table-bordered mb-2" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>
                                    <div className="d-flex align-items-center">
                                        {column.canGroupBy ? (
                                            // If the column can be grouped, let's add a toggle
                                            <span {...column.getGroupByToggleProps()}>{column.isGrouped ? "🛑 " : "👊 "}</span>
                                        ) : null}
                                        <span {...column.getSortByToggleProps()}>
                                            {column.render("Header")}
                                            {/* Add a sort direction indicator */}
                                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                        </span>
                                    </div>
                                    {/* Render the columns filter UI */}
                                    <div>{column.canFilter ? column.render("Filter") : null}</div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell, i) => {
                                    return (
                                        <td {...cell.getCellProps()}>
                                            {cell.isGrouped ? (
                                                // If it's a grouped cell, add an expander and row count
                                                <>
                                                    <span {...row.getToggleRowExpandedProps()}>
                                                        {row.isExpanded ? "👇" : "👉"}
                                                    </span>{" "}
                                                    {cell.render("Cell", { editable: false })} ({row.subRows.length})
                                                </>
                                            ) : cell.isAggregated ? (
                                                // If the cell is aggregated, use the Aggregated
                                                // renderer for cell
                                                cell.render("Aggregated")
                                            ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                                                // Otherwise, just render the regular cell
                                                cell.render("Cell", { editable: false })
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {/*
        Pagination can be built however you'd like.
        This is just a very basic UI implementation:
      */}

            <div className="d-flex align-items-center justify-content-center mb-3">
                {/* <div className="me-1 ms-2">Ir a pag:</div> */}
                <div className="w-50px mx-2 me-auto">
                    {/* <input
                        className="form-control"
                        type="number"
                        defaultValue={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            gotoPage(page);
                        }}
                    /> */}
                    <select
                        value={pageSize}
                        className="me-3"
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}>
                        {[15, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Mostrar {pageSize}
                            </option>
                        ))}
                    </select>
                </div>

                <ul className="pagination mb-0">
                    <li className="page-item">
                        <button className="btn btn-white btn-xs me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                            <i className="fa fa-angle-double-left"></i>
                        </button>
                    </li>
                    <li className="page-item">
                        <button className="btn btn-white btn-xs" onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <i className="fa fa-angle-left"></i>
                        </button>
                    </li>
                    <li className="page-item d-flex align-items-center px-2">
                        <div>
                            Pag{" "}
                            <strong>
                                {pageIndex + 1} de {pageOptions.length}
                            </strong>
                        </div>
                    </li>
                    <li className="page-item">
                        <button className="btn btn-white btn-xs me-1" onClick={() => nextPage()} disabled={!canNextPage}>
                            <i className="fa fa-angle-right"></i>
                        </button>
                    </li>
                    <li className="page-item">
                        <button
                            className="btn btn-white btn-xs me-2"
                            onClick={() => gotoPage(pageCount - 1)}
                            disabled={!canNextPage}>
                            <i className="fa fa-angle-double-right"></i>
                        </button>
                    </li>
                </ul>
            </div>

            {/* <pre>
                <code>
                    {JSON.stringify(
                        {
                            pageIndex,
                            pageSize,
                            pageCount,
                            canNextPage,
                            canPreviousPage,
                            sortBy,
                            groupBy,
                            expanded: expanded,
                            filters,
                            selectedRowIds: selectedRowIds,
                            "selectedFlatRows[].original": selectedFlatRows.map((d) => d.original),
                        },
                        null,
                        2
                    )}
                </code>
            </pre> */}
        </div>
    );
}

const IndeterminateCheckbox = React.forwardRef(({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
        <div>
            <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
        </div>
    );
});

function ReactTableComponent(props) {
    const [data, setData] = React.useState(props.data);
    const [originalData, setOriginalData] = React.useState(props.data);

    useEffect(() => {
        setData(props.data);
        setOriginalData(props.data);
    }, [props.data]);

    // We need to keep the table from resetting the pageIndex when we
    // Update data. So we can keep track of that flag with a ref.
    const skipResetRef = React.useRef(false);

    // When our cell renderer calls updateMyData, we'll use
    // the rowIndex, columnId and new value to update the
    // original data
    const updateMyData = (rowIndex, columnId, value) => {
        // We also turn on the flag to not reset the page
        skipResetRef.current = true;
        setData((old) =>
            old.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...row,
                        [columnId]: value,
                    };
                }
                return row;
            })
        );
    };

    // After data changes, we turn the flag back off
    // so that if data actually changes when we're not
    // editing it, the page is reset
    React.useEffect(() => {
        skipResetRef.current = false;
    }, [data]);

    // Let's add a data resetter/randomizer to help
    // illustrate that flow...
    const resetData = () => {
        // Don't reset the page when we do this
        skipResetRef.current = true;
        setData(originalData);
    };

    return (
        <div>
            {/* <button onClick={resetData}>Reset Data</button> */}
            <Table
                {...props}
                // columns={props.columns}
                // data={data}
                updateMyData={updateMyData}
                skipReset={skipResetRef.current}
            />
        </div>
    );
}

export { SelectColumnFilter, NumberRangeColumnFilter, multiSelectFilter, MultiSelectColumnFilter };

export default ReactTableComponent;
